/**
 * React Query key factory — EPIC 40 TND (appels d'offres & marchés publics).
 *
 * ⚠️ `entityId` entre dans les clés de LISTE et de FICHE : la réponse dépend du
 * viewer (périmètre d'entité, drapeaux `is_inherited` / `editable`). Sans lui,
 * changer d'entité rendrait le cache de la précédente — avec le pipeline d'une
 * autre maison.
 *
 * ## Le listing serveur a ses PROPRES clés, en plus des nôtres
 *
 * Le socle DataTable (`mode="server"`) met en cache sous la chaîne d'endpoint
 * brute — `['/api/sales/tenders']` et `['datatable-groups', '/api/sales/tenders']`.
 * Toute mutation d'appel d'offres doit donc invalider CES clés-là **en plus** de
 * `tenders.all`, sinon la table reste peinte sur des données périmées pendant
 * que la fiche, elle, s'est rafraîchie. Même doctrine que les devis.
 *
 * ## Ce qu'une mutation d'AO invalide au-delà de son propre module
 *
 * Un appel d'offres rattaché à un client apparaît dans le sous-onglet « Appels
 * d'offres » de sa fiche (TND-022) : `crmKeys.clients.activities` doit tomber
 * avec lui. Un AO transformé crée en outre des contrats cadres — donc les clés
 * du CRM. Oublier cette propagation laisse la fiche client annoncer un pipeline
 * qui n'existe plus.
 */

const tendersAll = ['tenders'] as const
const tenderBoardsAll = ['tenderBoards'] as const
const tenderStatusesAll = ['tenderStatuses'] as const
const tenderWatchAll = ['tenderWatch'] as const
const tenderLibraryAll = ['tenderLibrary'] as const
const tenderPochetteAll = ['tenderPochette'] as const
const marketIntelAll = ['marketIntel'] as const

/** Les clés du socle DataTable pour le listing serveur des AO. */
export const TENDERS_LISTING_ENDPOINT = '/api/sales/tenders'

export const tendersKeys = {
    tenders: {
        all: tendersAll,
        /** Listing serveur — filtres compris. */
        list: (entityId: string, filters?: Record<string, unknown>) =>
            [...tendersAll, 'list', entityId, filters] as const,
        /** En-tête + corps de la fiche. */
        detail: (id: string, entityId: string) => [...tendersAll, 'detail', id, entityId] as const,
        /** Préfixe d'invalidation cross-entité (matche toutes les variantes). */
        detailAny: (id: string) => [...tendersAll, 'detail', id] as const,
        /** Préfixe de TOUTES les fiches : une écriture CLIENT les périme toutes (nom, contact). */
        detailsAll: () => [...tendersAll, 'detail'] as const,
        /** Les 6 procédures de passation (TND-001) — référentiel PUBLIC, donc
         *  sans `entityId` : il est le même pour tous les organismes. */
        referentials: () => [...tendersAll, 'referentials'] as const,
        /** Bandeau de 4 tuiles au-dessus de la table, miroir de ses filtres. */
        kpis: (entityId: string, filters?: Record<string, unknown>) =>
            [...tendersAll, 'kpis', entityId, filters] as const,

        // ── Sous-ressources de la fiche ──────────────────────────────────────
        /** Lots (TND-002). */
        lots: (id: string) => [...tendersAll, 'lots', id] as const,
        /** Échéances (TND-006) — l'ordre de l'urgence. */
        deadlines: (id: string) => [...tendersAll, 'deadlines', id] as const,
        /** Les natures d'échéance que la BASE de l'organisme admet (TND-006, arbitrage A5) —
         *  sans entité ni AO : le CHECK sondé est celui du locataire. */
        deadlineKinds: () => [...tendersAll, 'deadlineKinds'] as const,
        /** Checklist des pièces (TND-007). `atDate` fait partie de la clé : la
         *  fraîcheur des attestations se juge à la date de REMISE prévue, pas
         *  à aujourd'hui — deux dates, deux réponses légitimement différentes. */
        pieces: (id: string, atDate?: string | null) =>
            [...tendersAll, 'pieces', id, atDate ?? null] as const,
        /** Racine des pièces — pour invalider quelle que soit la date jugée. */
        piecesRoot: (id: string) => [...tendersAll, 'pieces', id] as const,
        /** Ce que le service saurait GÉNÉRER pour UNE ligne de checklist :
         *  d'où vient le modèle (le DCE de l'acheteur, ou le nôtre daté), son
         *  âge, et les champs d'organisme qui manqueraient au formulaire.
         *
         *  ⚠️ Racine DISTINCTE de `pieces` : la réponse ne change pas quand une
         *  case « prête » est cochée, et l'y ranger la ferait recharger à
         *  chaque clic de la checklist — quarante lignes, quarante requêtes. */
        pieceGeneration: (id: string, pieceId: string) =>
            [...tendersAll, 'pieceGeneration', id, pieceId] as const,
        /** Dossier documentaire (TND-008) : arborescence + fichiers. */
        documents: (id: string, filters?: Record<string, unknown>) =>
            [...tendersAll, 'documents', id, filters ?? {}] as const,
        documentsRoot: (id: string) => [...tendersAll, 'documents', id] as const,
        /** Échanges avec l'acheteur (TND-009). */
        questions: (id: string) => [...tendersAll, 'questions', id] as const,
        /** Historique des étapes (TND-003 §7.4, bloc « Activité »). Une transition le relit
         *  par la racine (`invalidateTenders` périme `tenders.all`) ; rangé SOUS la fiche
         *  (`detailAny`), il est aussi relu par les écritures qui ne périment QUE la fiche
         *  (lots, équipe, checklist), sans que chaque mutation ait à le nommer. */
        statusEvents: (id: string, entityId: string) =>
            [...tendersAll, 'detail', id, 'status-events', entityId] as const,
        /** Bordereau de prix, toutes grilles groupées par lot (TND-010). */
        priceGrid: (id: string) => [...tendersAll, 'priceGrid', id] as const,
        /** Analyses IA du DCE (TND-016). */
        analyses: (id: string) => [...tendersAll, 'analyses', id] as const,
        analysis: (id: string, analysisId: string) =>
            [...tendersAll, 'analyses', id, analysisId] as const,
        /** Poids EN CARACTÈRES d'une sélection de pièces, avant de lancer.
         *
         *  ⚠️ Racine DISTINCTE de `analyses`, et pas une branche dessous :
         *  `analyses(id)` est le préfixe de `analysis(id, aid)`, et tout apply
         *  l'invalide. Une mesure rangée là-dessous serait jetée à chaque
         *  application — alors qu'elle ne dépend que des FICHIERS, que l'apply
         *  ne touche pas.
         *
         *  Les identifiants entrent dans la clé TRIÉS : cocher A puis B et
         *  cocher B puis A désignent la même sélection et doivent donc lire la
         *  même mesure. Sans le tri, un simple décochage/recochage relançait la
         *  lecture de huit PDF. */
        analysisSize: (id: string, gedFileIds: readonly string[]) =>
            [...tendersAll, 'analysisSize', id, [...gedFileIds].sort()] as const,
        /** Équipe pressentie + proposition de matching (TND-020). */
        team: (id: string) => [...tendersAll, 'team', id] as const,
        teamMatching: (id: string) => [...tendersAll, 'team', id, 'matching'] as const,
        /** État du CV de chaque membre — le MIROIR lu par l'onglet Documents. */
        teamCvs: (id: string) => [...tendersAll, 'team', id, 'cvs'] as const,
        /** Qui l'on peut désigner chargé / responsable du marché : sur l'entité de l'AO
         *  (`tenderId`), ou de la session (`null`, la masse) — décision PO n° 66. */
        designables: (entityId: string, tenderId: string | null) =>
            [...tendersAll, 'designables', entityId, tenderId] as const,
        // `market: (id)` a été RETIRÉE avec l'aide au prix (arbitrage PO du
        // 2026-09-08) : elle désignait les COMPARABLES d'une suggestion, une
        // notion qui n'existe plus ni côté route ni côté dépôt. Elle n'avait
        // aucun appelant (vérifié par `grep` sur `.market(` : seul
        // `tenderLifecycleUrl.market(...)`, qui est une URL, répondait) — une
        // clé sans appelant est une invitation à recâbler un écran sur une
        // route morte.
        /** Sélecteur de blocs et de références pour composer un mémoire. */
        libraryPicker: (id: string) => [...tendersAll, 'libraryPicker', id] as const,

        /**
         * Racine des statistiques d'une entité — TOUTES ses fenêtres à la fois.
         *
         * ⚠️ C'est la clé d'INVALIDATION, et `stats(entityId)` ne peut pas en
         * tenir lieu : celle-ci finit par `{}`, qui est un SEGMENT de clé et non
         * un joker. Une invalidation posée dessus ne toucherait donc que la
         * fenêtre sans paramètre — c'est-à-dire aucune de celles qu'un écran
         * affiche, et le rafraîchissement paraîtrait sans effet.
         */
        statsRoot: (entityId: string) => [...tendersAll, 'stats', entityId] as const,
        /** Tableau de bord (TND-021) — la période fait partie de la clé. */
        stats: (entityId: string, params?: Record<string, unknown>) =>
            [...tendersAll, 'stats', entityId, params ?? {}] as const,
        /** Disponibilité de l'onglet : masqué tant qu'aucun AO n'est décidé. */
        statsAvailability: (entityId: string) =>
            [...tendersAll, 'stats', entityId, 'availability'] as const,
    },

    /** Kanban (TND-005) — boards, colonnes et tuiles paginées par colonne. */
    tenderBoards: {
        all: tenderBoardsAll,
        list: (entityId: string) => [...tenderBoardsAll, 'list', entityId] as const,
        summary: (boardId: string, filters?: Record<string, unknown>) =>
            [...tenderBoardsAll, 'summary', boardId, filters ?? {}] as const,
        column: (boardId: string, columnId: string, filters?: Record<string, unknown>) =>
            [...tenderBoardsAll, 'column', boardId, columnId, filters ?? {}] as const,
        /** Racine d'un board — invalide ses en-têtes ET toutes ses colonnes. */
        board: (boardId: string) => [...tenderBoardsAll, boardId] as const,
    },

    /** Référentiels de pipeline (TND-003) : statuts et motifs d'issue. */
    tenderStatuses: {
        all: tenderStatusesAll,
        list: (entityId: string) => [...tenderStatusesAll, 'list', entityId] as const,
        outcomeReasons: (entityId: string) =>
            [...tenderStatusesAll, 'outcomeReasons', entityId] as const,
    },

    /** Veille BOAMP (TND-017) : profils et avis captés. */
    tenderWatch: {
        all: tenderWatchAll,
        profiles: (entityId: string) => [...tenderWatchAll, 'profiles', entityId] as const,
        summary: (entityId: string) => [...tenderWatchAll, 'summary', entityId] as const,
        items: (entityId: string, filters?: Record<string, unknown>) =>
            [...tenderWatchAll, 'items', entityId, filters ?? {}] as const,
    },

    /** Bibliothèque de réponse (TND-019) : références de marché et blocs. */
    tenderLibrary: {
        all: tenderLibraryAll,
        references: (entityId: string, filters?: Record<string, unknown>) =>
            [...tenderLibraryAll, 'references', entityId, filters ?? {}] as const,
        blocks: (entityId: string, filters?: Record<string, unknown>) =>
            [...tenderLibraryAll, 'blocks', entityId, filters ?? {}] as const,
        /** TND-019 §0.10 — le détail par niveau d'UNE référence. */
        volumetry: (entityId: string, referenceId: string) =>
            [...tenderLibraryAll, 'volumetry', entityId, referenceId] as const,
    },

    /**
     * Pochette de pièces permanentes — les pièces de l'ORGANISME (Kbis,
     * attestations, assurance…), déposées une fois et reprises dans chaque
     * consultation.
     *
     * ⚠️ Elle n'est PAS sous `tenders.*` : elle ne dépend d'aucun appel
     * d'offres, et l'y ranger la ferait tomber à chaque mutation de fiche —
     * un rechargement par clic sur une liste qui ne bouge que deux fois l'an.
     * Symétriquement, REPRENDRE une pièce dans une consultation invalide, elle,
     * `tenders.piecesRoot(tenderId)` : c'est la checklist qui change, pas la
     * pochette.
     */
    tenderPochette: {
        all: tenderPochetteAll,
        /** `atDate` fait PARTIE de la clé : la validité d'une attestation se
         *  juge au jour de la REMISE prévue, pas à aujourd'hui — deux dates,
         *  deux réponses légitimement différentes. Même doctrine que
         *  `tenders.pieces`. */
        list: (entityId: string, atDate?: string | null) =>
            [...tenderPochetteAll, 'list', entityId, atDate ?? null] as const,
        /** Racine — pour invalider quelle que soit la date jugée. */
        racine: (entityId: string) => [...tenderPochetteAll, 'list', entityId] as const,
        /**
         * La FICHE DE PRÉPARATION du DUME — les réponses récurrentes de
         * l'organisme, à recopier sur la plateforme de l'acheteur.
         *
         * ⚠️ Clé DISTINCTE de `list` : elle ne lit pas la pochette mais la fiche
         * de l'ORGANISME, et aucune mutation de pochette ne la périme. La ranger
         * sous `list` la ferait recharger à chaque dépôt de Kbis — et surtout,
         * `racine()` la balaierait sans que rien ne l'ait changée.
         */
        ficheDume: (entityId: string) => [...tenderPochetteAll, 'ficheDume', entityId] as const,
    },

    /** Intelligence marché (TND-018) — cache PUBLIC cross-tenant : la clé ne
     *  porte donc PAS d'entité, seulement les critères de recherche. */
    marketIntel: {
        all: marketIntelAll,
        awards: (params?: Record<string, unknown>) =>
            [...marketIntelAll, 'awards', params ?? {}] as const,
    },
}
